// @/services/messenger.js
import { ref } from 'vue'
import { API_MESSENGER_URL } from '@/services/api' // Import from api.js

export const useMessenger = () => {
  const messenger = ref(null)
  const isConnected = ref(false)
  const isLoading = ref(false)
  const error = ref(null)
  
  // Create messenger client (simplified version of your original)
  const createClient = (username, serverUrl) => {
    class MessengerClient {
      constructor(username, serverUrl) {
        this.username = username
        this.serverUrl = serverUrl
        this.peerId = this.generatePeerId(username)
        this.isConnected = false
        this.messageCallbacks = []
        this.pollInterval = null
      }
      
      generatePeerId(username) {
        const timestamp = Date.now()
        const random = Math.random().toString(36).substring(2, 15)
        const hashInput = `${username}_${timestamp}_${random}`
        let hash = 0
        for (let i = 0; i < hashInput.length; i++) {
          hash = ((hash << 5) - hash) + hashInput.charCodeAt(i)
          hash = hash & hash
        }
        return Math.abs(hash).toString(16).substring(0, 16)
      }
      
      onMessageReceived(callback) {
        this.messageCallbacks.push(callback)
      }
      
      async connect() {
        try {
          isLoading.value = true
          
          // Register with server
          const response = await fetch(`${this.serverUrl}/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              peer_id: this.peerId,
              username: this.username,
              public_key: 'temp_key', // In real app, generate proper key
              timestamp: new Date().toISOString()
            })
          })
          
          if (response.ok) {
            this.isConnected = true
            isConnected.value = true
            
            // Start polling for messages
            this.startPolling()
            
            console.log(`Messenger connected as ${this.username}`)
            return true
          } else {
            throw new Error('Registration failed')
          }
        } catch (err) {
          error.value = err.message
          return false
        } finally {
          isLoading.value = false
        }
      }
      
      startPolling() {
        if (this.pollInterval) clearInterval(this.pollInterval)
        
        this.pollInterval = setInterval(async () => {
          if (!this.isConnected) return
          
          try {
            // Check for messages
            const response = await fetch(`${this.serverUrl}/unread/${this.peerId}`)
            if (response.ok) {
              const messages = await response.json()
              if (messages.length > 0) {
                console.log(`Received ${messages.length} messages via polling`)
                this.processMessages(messages)
                
                // Acknowledge messages
                await this.acknowledgeMessages(messages.map(m => m.message_id))
              }
            }
          } catch (err) {
            console.error('Polling error:', err)
          }
        }, 3000)
      }
      
      async sendMessage(recipientUsername, messageText) {
        try {
          // Find recipient
          const findResponse = await fetch(`${this.serverUrl}/find/${recipientUsername}`)
          if (!findResponse.ok) {
            throw new Error('Recipient not found')
          }
          
          const recipientInfo = await findResponse.json()
          
          // Generate message ID
          const messageId = this.generateMessageId()
          
          // Create message packet (simplified - no encryption in this example)
          const messagePacket = {
            type: 'message',
            message_id: messageId,
            sender: this.username,
            sender_peer_id: this.peerId,
            recipient_peer_id: recipientInfo.peer_id,
            recipient_username: recipientUsername,
            encrypted_data: btoa(encodeURIComponent(messageText)),
            timestamp: new Date().toISOString(),
            public_key: 'temp_key'
          }
          
          // Send to server
          const response = await fetch(`${this.serverUrl}/deliver`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(messagePacket)
          })
          
          if (!response.ok) {
            throw new Error('Failed to send message')
          }
          
          console.log('Message sent successfully')
          return { success: true, messageId }
          
        } catch (err) {
          console.error('Send message error:', err)
          return { success: false, error: err.message }
        }
      }
      
      generateMessageId() {
        return Math.random().toString(36).substring(2, 15) + 
               Math.random().toString(36).substring(2, 15)
      }
      
      processMessages(messages) {
        messages.forEach(msg => {
          try {
            // Decode message (simplified)
            let text = msg.encrypted_data
            try {
              text = decodeURIComponent(atob(msg.encrypted_data))
            } catch {
              // If decoding fails, use as-is
            }
            
            // Call all registered callbacks
            this.messageCallbacks.forEach(callback => {
              callback({
                text,
                sender: msg.sender,
                timestamp: msg.timestamp,
                messageId: msg.message_id
              })
            })
          } catch (err) {
            console.error('Error processing message:', err)
          }
        })
      }
      
      async acknowledgeMessages(messageIds) {
        if (messageIds.length === 0) return
        
        try {
          await fetch(`${this.serverUrl}/ack`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              peer_id: this.peerId,
              message_ids: messageIds
            })
          })
        } catch (err) {
          console.error('Acknowledge error:', err)
        }
      }
      
      disconnect() {
        if (this.pollInterval) {
          clearInterval(this.pollInterval)
          this.pollInterval = null
        }
        this.isConnected = false
        isConnected.value = false
        console.log('Messenger disconnected')
      }
    }
    
    return new MessengerClient(username, serverUrl)
  }
  
  const connect = async (username, serverUrl = API_MESSENGER_URL) => {
    messenger.value = createClient(username, serverUrl)
    return await messenger.value.connect()
  }
  
  const sendMessage = async (recipient, message) => {
    if (!messenger.value) {
      throw new Error('Messenger not connected')
    }
    return await messenger.value.sendMessage(recipient, message)
  }
  
  const disconnect = () => {
    if (messenger.value) {
      messenger.value.disconnect()
      messenger.value = null
    }
  }
  
  return {
    messenger,
    isConnected,
    isLoading,
    error,
    connect,
    sendMessage,
    disconnect
  }
}